import { FunctionComponent, useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider } from 'react-router';
import { SWRConfig, SWRConfiguration } from 'swr';
import LegacyRedirect from '../common/components/LegacyRedirect';
import ErrorBoundary from '../common/error/ErrorBoundary';
import RouteErrorBoundary from '../common/error/RouteErrorBoundary';
import { axiosFetcher } from '../common/utils/apiHelper';
import { withConfig } from '../common/utils/loaderFactory';
import { advertRoutes } from '../features/adverts/advertRoutes';
import { assetRoutes } from '../features/assets/assetRoutes';
import AccessDenied from '../features/authentication/AccessDenied';
import { AuthProvider } from '../features/authentication/AuthContext';
import AuthService from '../features/authentication/AuthService';
import AuthSignInCallback from '../features/authentication/AuthSignInCallback';
import AuthSignOut from '../features/authentication/AuthSignOut';
import AuthSignOutCallback from '../features/authentication/AuthSignOutCallback';
import AuthSilentRenew from '../features/authentication/AuthSilentRenew';
import { bookingRoutes } from '../features/bookings/bookingRoutes';
import { ConfigProvider } from '../features/configuration/ConfigContext';
import { Configuration } from '../features/configuration/Configuration';
import { getConfiguration } from '../features/configuration/configService';
import Invitation from '../features/invitation/Invitation';
import { memberRoutes } from '../features/members/memberRoutes';
import { messageRoutes } from '../features/messages/messageRoutes';
import MobileNavigation from '../features/mobileNavigation/MobileNavigation';
import Navigation from '../features/navigation/Navigation';
import { PropertyAssociationProvider } from '../features/propertyAssociation/PropertyAssociationContext';
import { PropertyAssociationLayout, propertyAssociationLoader } from '../features/propertyAssociation/PropertyAssociationLayout';
import { settingRoutes } from '../features/settings/settingRoutes';
import { ToastProvider } from '../features/toaster/ToastContext';
import Toaster from '../features/toaster/Toaster';
import Home from '../pages/Home';
import NoSubscription from '../pages/NoSubscription';
import NotFound from '../pages/NotFound';
import PropertyAssociation from '../pages/PropertyAssociation';
import PropertyAssociationArea from '../pages/PropertyAssociationArea';
import TrialHasEnded from '../pages/TrialHasEnded';
import { AppLayout, appLoader } from './AppLayout';

const emptyPropertyAssociationContext = {
    allPropertyAssociations: [],
    allAreas: [],
    currentArea: undefined,
    currentFunctionPermissions: undefined,
    currentPropertyAssociation: undefined,
    currentPropertyAssociationId: '',
    currentPropertyOwner: undefined,
    isAdmin: false,
    isGuest: true,
    isRBHyr: false,
};

// Access denied layout - shows navigation but doesn't require data loader
const AccessDeniedLayout: FunctionComponent = () => {
    return (
        <PropertyAssociationProvider value={emptyPropertyAssociationContext}>
            <Navigation />
            <MobileNavigation />
            <div id="content" className="app__content">
                <AccessDenied />
            </div>
        </PropertyAssociationProvider>
    );
};

// Root wrapper component with all providers
const RootWrapper: FunctionComponent<{ config: Configuration; authService: AuthService }> = ({ config, authService }) => {
    return (
        <ErrorBoundary key="global">
            <ConfigProvider config={config}>
                <AuthProvider config={config} authService={authService}>
                    <ToastProvider>
                        <Outlet />
                        <Toaster />
                    </ToastProvider>
                </AuthProvider>
            </ConfigProvider>
        </ErrorBoundary>
    );
};

// Create the router with hybrid structure
const createAppRouter = (config: Configuration, authService: AuthService) => {
    return createBrowserRouter(
        createRoutesFromElements(
            <Route element={<RootWrapper config={config} authService={authService} />}>
                {/* Routes without navigation */}
                <Route
                    path="/invitations/:id"
                    element={
                        <ErrorBoundary key="/invitation">
                            <Invitation />
                        </ErrorBoundary>
                    }
                />
                <Route
                    path="/trial-has-ended/"
                    element={
                        <ErrorBoundary key="/trial-has-ended">
                            <TrialHasEnded />
                        </ErrorBoundary>
                    }
                />
                <Route
                    path="/no-subscription/"
                    element={
                        <ErrorBoundary key="/no-subscription">
                            <NoSubscription />
                        </ErrorBoundary>
                    }
                />

                {/* Auth routes */}
                <Route path="/signin-oidc" element={<AuthSignInCallback />} />
                <Route path="/signout-oidc" element={<AuthSignOut />} />
                <Route path="/signout-callback-oidc" element={<AuthSignOutCallback />} />
                <Route path="/silentrenew" element={<AuthSilentRenew />} />

                {/* Access denied - with navigation but no data loader to avoid infinite loop on 401/403 */}
                <Route path="/access-denied" element={<AccessDeniedLayout />} />

                {/* Routes with navigation and main layout */}
                <Route
                    path="/"
                    element={
                        <ErrorBoundary key="/">
                            <AppLayout />
                        </ErrorBoundary>
                    }
                    loader={withConfig(appLoader, config, authService)}
                    errorElement={<RouteErrorBoundary />}
                >
                    {/* Legacy redirects */}
                    <Route path="assets/*" element={<LegacyRedirect />} />
                    <Route path="bookings/*" element={<LegacyRedirect />} />
                    <Route path="adverts/*" element={<LegacyRedirect />} />
                    <Route path="messages/*" element={<LegacyRedirect />} />
                    <Route path="members/*" element={<LegacyRedirect />} />
                    <Route path="settings/*" element={<LegacyRedirect />} />
                    <Route path="resources/*" element={<LegacyRedirect />} />

                    <Route index element={<Home />} />
                    {/* Hide release notes for now as they are not continuously updated and links might be incorrect */}
                    {/* <Route path="release-notes" element={<ErrorBoundary key="release-notes"><ReleaseNotes /></ErrorBoundary>} /> */}

                    {/* Property association nested routes */}
                    <Route
                        path=":propertyAssociationId"
                        element={
                            <ErrorBoundary key="propertyAssociation">
                                <PropertyAssociationLayout />
                            </ErrorBoundary>
                        }
                        loader={withConfig(propertyAssociationLoader, config, authService)}
                        errorElement={<RouteErrorBoundary />}
                    >
                        {/* Feature routes that don't require area context */}
                        <Route path="messages">{messageRoutes}</Route>
                        <Route path="members">{memberRoutes}</Route>
                        <Route path="settings">{settingRoutes}</Route>

                        <Route index element={<Navigate to="areas/" replace />} />
                        <Route path="areas/" element={<PropertyAssociation />} />
                        {/* Feature routes that require area context */}
                        <Route path="areas/:areaId">
                            <Route index element={<PropertyAssociationArea />} />
                            <Route path="assets">{assetRoutes}</Route>
                            <Route path="bookings">{bookingRoutes}</Route>
                            <Route path="adverts">{advertRoutes}</Route>
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Route>
            </Route>,
        ),
        { basename: '/' },
    );
};

const swrConfig: SWRConfiguration = {
    fetcher: axiosFetcher,
    shouldRetryOnError: false,
    dedupingInterval: 5000, // 5 seconds,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
};

const ManagerApp: FunctionComponent = () => {
    const [config, setConfig] = useState<Configuration>();
    const [authService, setAuthService] = useState<AuthService>();
    const [initializationError, setInitializationError] = useState(false);

    useEffect(() => {
        getConfiguration(
            (data) => {
                setConfig(data);
                const service = new AuthService(data);
                setAuthService(service);
            },
            () => setInitializationError(true),
        );
    }, []);

    if (initializationError) {
        return <div>Kunde inte ladda konfigurationen för applikationen!</div>;
    }

    if (!config || !authService) {
        return null;
    }

    const router = createAppRouter(config, authService);

    return (
        <SWRConfig value={swrConfig}>
            <RouterProvider router={router} />
        </SWRConfig>
    );
};

export default ManagerApp;
